import { Sentry } from '../sentry'
import { supabase } from '../supabase'
import { isTransientNetworkError } from '../transientNetworkError'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

export type BillingPlanCheckout = 'pro_monthly' | 'pro_annual' | 'pro_try_weekly' | 'overage_pack'

export interface BillingEntitlements {
  tier: 'free' | 'pro'
  remaining: number
  cap: number
  used: number
  allowed: boolean
  reason?: string | null
  subscription_active: boolean
  plan_kind?: string | null
  period_end?: string | null
  cancel_at_period_end?: boolean
  pack_credits_remaining?: number
  billing_enabled: boolean
  has_stripe_customer?: boolean
  quota_period?: 'lifetime' | 'billing_period' | 'calendar_month'
}

export interface EntitlementCheckResult {
  allowed: boolean
  code?: string | null
  reason?: string | null
  tier: string
  remaining: number
  cap: number
  used: number
  upgrade_url: string
}

export class QuotaExhaustedApiError extends Error {
  readonly code = 'quota_exhausted'
  readonly reason?: string | null
  readonly duplicateLayer?: string | null
  readonly tier?: string
  readonly remaining: number
  readonly cap: number

  constructor(payload: {
    message?: string
    reason?: string | null
    duplicate_layer?: string | null
    tier?: string
    remaining?: number
    cap?: number
  }) {
    super(payload.message || 'No optimizations remaining')
    this.reason = payload.reason
    this.duplicateLayer = payload.duplicate_layer
    this.tier = payload.tier
    this.remaining = payload.remaining ?? 0
    this.cap = payload.cap ?? 0
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`
  }
  return headers
}

export async function fetchBillingEntitlements(): Promise<BillingEntitlements | null> {
  if (entitlementsFetchInflight) {
    return entitlementsFetchInflight
  }
  entitlementsFetchInflight = fetchBillingEntitlementsOnce().finally(() => {
    entitlementsFetchInflight = null
  })
  return entitlementsFetchInflight
}

export async function checkBillingEntitlement(): Promise<EntitlementCheckResult | null> {
  const headers = await authHeaders()
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/billing/entitlement/check`, {
      method: 'POST',
      headers,
    })
    if (response.status === 503) return null
    if (!response.ok) {
      Sentry.captureMessage('Billing entitlement check failed', {
        level: 'warning',
        extra: { status: response.status },
      })
      return null
    }
    return (await response.json()) as EntitlementCheckResult
  } catch (error) {
    if (!isTransientNetworkError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'billing', endpoint: 'entitlement_check' },
      })
    }
    return null
  }
}

export async function createBillingCheckout(plan: BillingPlanCheckout): Promise<string> {
  const headers = await authHeaders()
  const response = await fetch(`${API_BASE_URL}/api/v1/billing/checkout-session`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      plan,
      return_origin: window.location.origin,
    }),
  })
  if (!response.ok) {
    const text = await response.text()
    throw new Error(text || `Checkout failed (${response.status})`)
  }
  const body = (await response.json()) as { url?: string }
  if (!body.url) throw new Error('Checkout URL missing')
  return body.url
}

export async function createBillingPortalSession(): Promise<string> {
  const headers = await authHeaders()
  const response = await fetch(`${API_BASE_URL}/api/v1/billing/portal-session`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ return_origin: window.location.origin }),
  })
  if (!response.ok) {
    const text = await response.text()
    throw new Error(text || `Portal session failed (${response.status})`)
  }
  const body = (await response.json()) as { url?: string }
  if (!body.url) throw new Error('Portal URL missing')
  return body.url
}

const billingEntitlementsInvalidateListeners = new Set<() => void>()

/** Coalesce burst invalidations (e.g. optimization start + block ticks). */
export const BILLING_ENTITLEMENTS_INVALIDATE_DEBOUNCE_MS = 2000

let billingInvalidateTimer: ReturnType<typeof setTimeout> | null = null
/** True when invalidates arrived during the debounce window — flush once on trailing edge. */
let billingInvalidateCoalescePending = false
let entitlementsFetchInflight: Promise<BillingEntitlements | null> | null = null

async function fetchBillingEntitlementsOnce(): Promise<BillingEntitlements | null> {
  const headers = await authHeaders()
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/billing/entitlements`, { headers })
    if (response.status === 503) return null
    if (!response.ok) {
      Sentry.captureMessage('Billing entitlements request failed', {
        level: 'warning',
        extra: { status: response.status },
      })
      return null
    }
    return (await response.json()) as BillingEntitlements
  } catch (error) {
    if (!isTransientNetworkError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'billing', endpoint: 'entitlements' },
      })
    }
    return null
  }
}

function notifyBillingEntitlementsInvalidate(): void {
  for (const listener of billingEntitlementsInvalidateListeners) {
    listener()
  }
}

function scheduleBillingInvalidateTrailing(): void {
  if (billingInvalidateTimer) {
    clearTimeout(billingInvalidateTimer)
  }
  billingInvalidateTimer = setTimeout(() => {
    billingInvalidateTimer = null
    if (billingInvalidateCoalescePending) {
      billingInvalidateCoalescePending = false
      notifyBillingEntitlementsInvalidate()
    }
  }, BILLING_ENTITLEMENTS_INVALIDATE_DEBOUNCE_MS)
}

/**
 * Notify QuotaChip and other subscribers to refetch `/billing/entitlements`.
 * Leading edge: first invalidate in a quiet period runs immediately (fast initial quota).
 * Trailing edge: burst invalidates during optimization coalesce to one follow-up fetch.
 */
export function invalidateBillingEntitlements(options?: { immediate?: boolean }): void {
  if (options?.immediate === true) {
    if (billingInvalidateTimer) {
      clearTimeout(billingInvalidateTimer)
      billingInvalidateTimer = null
    }
    billingInvalidateCoalescePending = false
    notifyBillingEntitlementsInvalidate()
    return
  }

  if (!billingInvalidateTimer) {
    notifyBillingEntitlementsInvalidate()
    billingInvalidateCoalescePending = false
    scheduleBillingInvalidateTrailing()
    return
  }

  billingInvalidateCoalescePending = true
  scheduleBillingInvalidateTrailing()
}

/** Reset debounce timer — test helper only. */
export function resetBillingEntitlementsInvalidateForTests(): void {
  if (billingInvalidateTimer) {
    clearTimeout(billingInvalidateTimer)
    billingInvalidateTimer = null
  }
  billingInvalidateCoalescePending = false
  entitlementsFetchInflight = null
}

export function subscribeBillingEntitlementsInvalidate(listener: () => void): () => void {
  billingEntitlementsInvalidateListeners.add(listener)
  return () => {
    billingEntitlementsInvalidateListeners.delete(listener)
  }
}

export function quotaChipLabel(entitlements: BillingEntitlements): string {
  const tierLabel = entitlements.tier === 'pro' ? 'Pro' : 'Free'
  const lifetimeSuffix =
    entitlements.tier === 'free' && entitlements.quota_period === 'lifetime'
      ? ' (lifetime cap)'
      : ''
  return `${entitlements.used} of ${entitlements.cap} used${lifetimeSuffix} · ${tierLabel}`
}
